import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import StatusBadge from "../../../components/RestaurantAdminComponents/StatusBadge";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import eyeIcon from "../../../assets/images/user-dashboard/eye.svg";

/* -------------------------------------------------------------------------
 * MarketInsightPage — full "Customer Insights" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMoney = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const pick = (obj, ...paths) => {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc === null || acc === undefined ? acc : acc[key]), obj);
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

const normalizeAmount = (value) => {
  if (value && typeof value === "object") {
    if (value.amount !== undefined && value.amount !== null) return value.amount;
    if (value.value !== undefined && value.value !== null) return value.value;
    if (value.cents !== undefined && value.cents !== null) return value.cents / 100;
    return null;
  }
  return value;
};

const normalizeTopCustomerAmount = (customer) => {
  const cents = pick(customer, "total_spend_cents", "spend_cents", "amount_spent_cents");
  if (cents !== null && cents !== undefined && cents !== "") return Number(cents) / 100;
  return Number(normalizeAmount(pick(customer, "total_spend", "total_spend.amount", "spend", "amount_spent")) || 0);
};

const topCustomerColumns = [
  {
    key: "customer",
    label: "Customer",
    width: "1.5fr",
    render: (r) => (
      <span className="rp-table__row-label">
        {r.avatarUrl ? (
          <img src={r.avatarUrl} alt="" className="rp-avatar" />
        ) : (
          <div className="rp-avatar rp-avatar--placeholder">
            {r.name ? r.name.charAt(0).toUpperCase() : "?"}
          </div>
        )}
        <div>{r.name}</div>
      </span>
    ),
  },
  { key: "email", label: "Email", width: "1.4fr" },
  { key: "type", label: "Type", width: "0.8fr", render: (r) => <StatusBadge label={r.type} /> },
  { key: "totalSpend", label: "Total Spend", width: "1fr", render: (r) => r.totalSpendDisplay },
  { key: "totalVisits", label: "Total Visits", width: "0.8fr", align: "right" },
  { key: "pointsEarned", label: "Points Earned", width: "1fr", align: "right", render: (r) => Number(r.pointsEarned || 0).toLocaleString() },
  { key: "lastVisit", label: "Last Visit", width: "1fr", render: (r) => r.lastVisit || "N/A" },
];

const MarketInsightPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Market Insights | Omaha Food Lovers";
  const navigate = useNavigate();
  
  const columns = [
    { key: "customerName", label: "Customer Name", width: "1.5fr" },
    { key: "customerEmail", label: "Email", width: "1.5fr" },
    { key: "merchantName", label: "Merchant", width: "1.5fr" },
    { key: "totalSpend", label: "Total Spend", width: "1fr", render: (r) => r.totalSpendDisplay },
    { key: "totalTransactions", label: "Transactions", width: "0.8fr", align: "right" },
    { key: "totalVisits", label: "Visits", width: "0.8fr", align: "right" },
    // { key: "pointsEarned", label: "Points Earned", width: "0.8fr", align: "right" },
    { key: "pointsBalance", label: "Points Balance", width: "0.8fr", align: "right" },
    { key: "lastTransactionAt", label: "Last Transaction", width: "1.2fr" },
    {
      key: "view",
      label: "",
      width: "64px",
      align: "center",
      render: (row) => (
        <img
          src={eyeIcon}
          alt="View"
          onClick={() => handleViewDetail(row)}
          style={{ cursor: 'pointer', width: '20px', height: '20px' }}
        />
      ),
    },
  ];
  const [marketInsights, setMarketInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [period, setPeriod] = useState("last_30_days");
  const [dateRange, setDateRange] = useState({ preset: "custom", start: "", end: "" });
  const [topCustomers, setTopCustomers] = useState([]);
  const [topCustomersLoading, setTopCustomersLoading] = useState(true);
  const [topCustomerSearch, setTopCustomerSearch] = useState("");
  const abortControllerRef = useRef(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const buildQueryParams = useCallback((pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("period", period);

    if (period === "custom") {
      if (dateRange?.start) params.append("date_from", dateRange.start);
      if (dateRange?.end) params.append("date_to", dateRange.end);
    }

    if (debouncedSearch.trim()) {
      params.append("search", debouncedSearch.trim());
    }

    return params.toString();
  }, [period, dateRange, debouncedSearch]);

  const transformMarketInsightsData = useCallback((data) => {
    if (!data) return [];

    return data.map((item, index) => ({
      id: item.customer_id || item.id || `${item.customer_name}_${item.merchant_name}_${index}`,
      customerName: item.customer_name || "Unknown",
      customerEmail: item.customer_email || "Unknown",
      customerMobile: item.customer_mobile || "Unknown",
      merchantName: item.merchant_name || item.restaurant_name || "Unknown",
      restaurantId: item.restaurant_id || item.merchant_id || null,
      category: item.category || "Unknown",
      totalSpend: item.total_spent || 0,
      totalSpendDisplay: item.total_spent_display || `$${(item.total_spent || 0).toFixed(2)}`,
      totalTransactions: item.total_transactions || 0,
      totalVisits: item.total_visits || 0,
      // pointsEarned: item.points_earned || 0,
      pointsBalance: item.points_balance || 0,
      lastTransactionAt: (item.last_transaction_at || item.last_visit_at) ? new Date(item.last_transaction_at || item.last_visit_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }) : "Unknown",
    }));
  }, []);

  const mapTopCustomers = useCallback((payload) => {
    const customersPayload = payload?.customers && typeof payload.customers === "object" && !Array.isArray(payload.customers)
      ? payload.customers.data
      : payload?.customers;
    const customersArray = Array.isArray(customersPayload)
      ? customersPayload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const seen = new Set();

    return customersArray.reduce((rows, customer, index) => {
      const id = customer.id ?? customer.user_id ?? customer.customer_id ?? index;
      if (seen.has(id)) return rows;
      seen.add(id);

      const name = customer.name || customer.full_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Unknown";
      const totalSpend = normalizeTopCustomerAmount(customer);

      rows.push({
        id,
        avatarUrl: customer.avatar_url || customer.profile_image || null,
        name,
        email: customer.email || "N/A",
        type: customer.customer_type_label || customer.customer_type || customer.status_label || customer.status || "Unknown",
        totalSpend,
        totalSpendDisplay: customer.total_spend_display || formatMoney(totalSpend),
        totalVisits: customer.visits ?? customer.total_visits ?? customer.transaction_count ?? 0,
        pointsEarned: customer.points_earned ?? customer.total_points_earned ?? customer.points ?? 0,
        firstVisit: formatDate(customer.first_visit_at),
        lastVisit: formatDate(customer.last_visit_at || customer.latest_visit_at || customer.last_transaction_at),
      });

      return rows;
    }, []);
  }, []);

  const fetchTopCustomers = useCallback(async () => {
    setTopCustomersLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      const response = await get(`${endpoints.restaurantDashboardTopCustomers}?${params.toString()}`);
      if (response.data?.success) {
        setTopCustomers(mapTopCustomers(response.data.data));
      } else {
        setTopCustomers([]);
      }
    } catch (error) {
      console.error("Failed to fetch top customers:", error);
      setTopCustomers([]);
    } finally {
      setTopCustomersLoading(false);
    }
  }, [dateRange, mapTopCustomers, period]);

  const filteredTopCustomers = useMemo(() => {
    const query = topCustomerSearch.trim().toLowerCase();
    if (!query) return topCustomers;

    return topCustomers.filter((customer) =>
      [customer.name, customer.email].some((value) =>
        String(value || "").toLowerCase().includes(query)
      )
    );
  }, [topCustomers, topCustomerSearch]);

  const fetchMarketInsights = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const queryParams = buildQueryParams(pageNum);
      const url = queryParams 
        ? `${endpoints.restaurantMarketInsightsShareOfWallet}?${queryParams}`
        : endpoints.restaurantMarketInsightsShareOfWallet;
      const response = await get(url, { signal: abortControllerRef.current.signal });

      if (response.data?.success) {
        const data = response.data.data;
        const rawData = data.rows?.data || data.data || data.locations || data.insights || data.records || [];
        const newInsights = transformMarketInsightsData(rawData);

        setMarketInsights((prev) => {
          if (!isLoadMore || pageNum === 1) {
            return newInsights;
          }
          const existingIds = new Set(prev.map(item => item.id));
          const uniqueNewInsights = newInsights.filter(item => !existingIds.has(item.id));
          return [...prev, ...uniqueNewInsights];
        });

        const pagination = data.rows || data.pagination || {};
        const currentPage = pagination.current_page || pageNum;
        const lastPage = pagination.last_page || 1;
        setHasMore(currentPage < lastPage);
        setPage(currentPage);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch customer insights:", error);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      abortControllerRef.current = null;
    }
  }, [buildQueryParams, transformMarketInsightsData]);

  const resetPagination = useCallback(() => {
    setPage(1);
    setHasMore(true);
    setMarketInsights([]);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchMarketInsights(page + 1, true);
    }
  }, [loadingMore, hasMore, page, fetchMarketInsights]);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
  }, []);

  const handlePeriodChange = useCallback((val) => {
    setPeriod(val);
    resetPagination();
    if (val !== "custom") {
      setDateRange({ preset: "custom", start: "", end: "" });
    }
  }, [resetPagination]);

  const handleDateRangeChange = useCallback((val) => {
    setDateRange(val);
    resetPagination();
  }, [resetPagination]);

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.restaurantExport}?type=customer_insights`, {
        responseType: 'blob',
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer_insights_export.csv');
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toastr.success('Customer insights exported successfully');
    } catch (error) {
      console.error('Failed to export customer insights:', error);
      toastr.error('Failed to export customer insights');
    }
  };

  const filters = useMemo(() => [
    {
      key: "period",
      type: "sortBy",
      label: "Period",
      value: period,
      onChange: handlePeriodChange,
      defaultValue: "last_30_days",
      options: [
        { value: "last_7_days", label: "Last 7 Days" },
        { value: "last_30_days", label: "Last 30 Days" },
        { value: "this_month", label: "This Month" },
        { value: "year_to_date", label: "Year to Date" },
        { value: "all_time", label: "All Time" },
        { value: "custom", label: "Custom Range" },
      ],
    },
    ...(period === "custom" ? [{
      key: "dateRange",
      type: "dateRange",
      label: "Date Range",
      value: dateRange,
      onChange: handleDateRangeChange,
      defaultValue: { preset: "custom", start: "", end: "" },
      hidePresets: true,
    }] : []),
  ], [period, dateRange, handlePeriodChange, handleDateRangeChange]);

  const handleViewDetail = useCallback((row) => {
    const queryParams = new URLSearchParams({
      merchant_name: row.merchantName,
      customer_id: row.id,
    });
    navigate(`/restaurant/market-insight/detail?${queryParams.toString()}`);
  }, [navigate]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      resetPagination();
    }, 500);

    return () => clearTimeout(timer);
  }, [search, resetPagination]);

  // Fetch data when debounced search, period, or dateRange changes
  useEffect(() => {
    fetchMarketInsights(1, false);
  }, [debouncedSearch, period, dateRange, fetchMarketInsights]);

  useEffect(() => {
    fetchTopCustomers();
  }, [fetchTopCustomers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Customer Insights</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of customer spending patterns and market trends.</p>
          </div>

          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search"
            filters={filters}
            showExport
            onExport={handleExport}
          />

          <DataTable
            columns={columns}
            rows={marketInsights}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />

          <style>
            {`
              .mi-top-customers-card {
                margin-top: 24px;
              }
              .mi-top-customers-card .rp-card__header {
                align-items: flex-start;
                gap: 16px;
              }
              .mi-top-customers-search {
                margin-left: auto;
                width: min(280px, 100%);
              }
              .mi-top-customers-search input {
                width: 100%;
                height: 40px;
                border: 1px solid #dfe3e8;
                border-radius: 8px;
                padding: 0 12px;
                font-size: 14px;
                background: #fff;
              }
              .mi-top-customers-empty {
                padding: 24px;
                text-align: center;
                color: #6b7280;
                font-size: 14px;
              }
              .mi-top-customers-card .rp-table {
                height: auto !important;
                max-height: none !important;
                overflow-y: visible !important;
              }
              .mi-top-customers-card .rp-table__empty {
                padding: 20px;
              }
              .mi-top-customers-card .rp-table__empty-icon {
                font-size: 28px;
                margin-bottom: 8px;
              }
              @media (max-width: 767px) {
                .mi-top-customers-card .rp-card__header {
                  flex-direction: column;
                }
                .mi-top-customers-search {
                  margin-left: 0;
                  width: 100%;
                }
              }
            `}
          </style>

          <div className="rp-card mi-top-customers-card">
            <div className="rp-card__header">
              <div>
                <h2 className="rp-card__title">Top Customers</h2>
                <p className="rp-card__subtitle">Highest spending members at your location.</p>
              </div>
              <div className="mi-top-customers-search">
                <input
                  type="search"
                  value={topCustomerSearch}
                  onChange={(event) => setTopCustomerSearch(event.target.value)}
                  placeholder="Search name or email"
                  aria-label="Search top customers by name or email"
                />
              </div>
            </div>
            {topCustomers.length > 0 && filteredTopCustomers.length === 0 && !topCustomersLoading ? (
              <div className="mi-top-customers-empty">No matching customers found.</div>
            ) : (
              <DataTable
                columns={topCustomerColumns}
                rows={filteredTopCustomers}
                getRowKey={(row) => row.id}
                loading={topCustomersLoading}
                compact
              />
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

MarketInsightPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

MarketInsightPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default MarketInsightPage;
